import {isFeatureEnabled} from 'feature-flags';
import {Avatar, Card, FlagProps, Flags, Separator, TabProps, Tooltip} from 'platform/components';
import {
  Box,
  Clickable,
  Heading,
  HStack,
  Show,
  Space,
  Stack,
  Text,
  VStack,
} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {useEffect, useRef, useState} from 'react';

import {defaultTo, find, isNil} from 'ramda';
import {concatAll, isFalse, isFalsy, isNotNil, isPositive} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {GetMetadaServiceCaseApiResponse} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  Checklists,
  CommentsWidget,
  EMPTY_PLACEHOLDER,
  ORDER_TABS,
  RecordAudits,
  queryParams,
  refreshDmsDatagrid,
  useChecklist,
  useRecordAuditsBadge,
  useTenant,
  useGetCurrentBranch,
  usePermissions,
} from '@dms/shared';

import {
  buildArray,
  DOT_CHARACTER,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useQueryState,
} from 'shared/web';

import {MetadaNoteWidget} from '../../../../../components/MetadaNoteWidget';
import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {ServiceCaseOrder} from '../../../../../types/OrderType';
import {useOrderCardData} from '../../../hooks/useOrderCardData';
import {useOrderCardFlags} from '../hooks/useOrderCardFlags';
import {getPartsMovementsTabIcon} from '../utils/getPartsMovementsTabIcon';
import {OrderDetailFormRef} from '../utils/orderDetailFormRef';
import {ActivityLogDataGrid} from './ActivityLogDataGrid';
import {Checkout} from './Checkout';
import {Discounts} from './Discounts';
import {OrderActions} from './OrderActions';
import {OrderCardTabs} from './OrderCardTabs';
import {OrderDetail} from './OrderDetail';
import {OrderInformation} from './OrderInformation';
import {OrderItemsDataGrid} from './OrderItemsDataGrid';
import {OrderJobs} from './OrderJobs';
import {PartsMovements} from './PartsMovements';
import {PriceDetailTooltip} from './PriceDetailTooltip';
import {ProfitabilityDataGrid} from './ProfitabilityDataGrid';
import {ProfitabilitySummary} from './ProfitabilitySummary';
import {ReadyToInvoiceSwitch} from './ReadyToInvoiceSwitch';
import {ServiceOrderDocuments} from './ServiceOrderDocuments';

const OVERPRICED_THRESHOLD = 10;
const PERCENTAGE_MULTIPLIER = 100;
const DIFFERENCE_OFFSET = 1;

interface OrderCardProps extends RequiredTestIdProps {
  defaultServiceOrder: ServiceCaseOrder;
  serviceOrderId: string;
  serviceCase: GetMetadaServiceCaseApiResponse;
  isExpanded: boolean;
  isOrderDataRefetching: boolean;
  dataGridKey: number;
  canCancelServiceCase: boolean;
  onExpand: () => void;
  onDataChange: () => void;
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function OrderCard(props: OrderCardProps) {
  const tenant = useTenant();
  const [orderTabId, setOrderTabId] = useQueryState(queryParams.SERVICE_CASE_ORDER_TAB);
  const {serviceCaseId, orderId} = useWorkshopUrl();
  const orderDetailRef = useRef<OrderDetailFormRef>(null);
  const {activeBranchId} = useGetCurrentBranch();
  // Owned here because both the actions menu and the header switch must stay disabled while the
  // order form saves.
  const [isSaveRequestPending, setIsSaveRequestPending] = useState(false);

  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();

  const {
    serviceOrder,
    integrations,
    variants,
    isLoading,
    isError,
    isBootstrapSettled,
    isIntegrationsLoading,
    isIntegrationsError,
  } = useOrderCardData({
    serviceCaseId,
    serviceOrderId: props.serviceOrderId,
    isExpanded: props.isExpanded,
    serviceOrderVariantId: props.defaultServiceOrder?.serviceOrderVariant?.serviceOrderVariantId,
  });

  const orderVariant = find(
    (variant) =>
      props.defaultServiceOrder?.serviceOrderVariant?.serviceOrderVariantId ===
      variant?.serviceOrderVariantId,
    variants ?? []
  );
  const type = orderVariant?.serviceOrderType?.type;
  const serviceCaseTypeId = props.serviceCase?.serviceCaseTypeId ?? '';
  const serviceOrderTypeId = orderVariant?.serviceOrderType?.serviceOrderTypeId ?? '';

  const [
    canReadChronos,
    canEditServiceOrder,
    canEditSOCustomDiscount,
    canEditSOCustomerContract,
    canReadServiceOrderFinancesProfitability,
    canEditSOVehicleClass,
    canEditSOCustomSmallUnitMaterial,
    canCancelServiceOrder,
  ] = usePermissions({
    permissionKeys: [
      'chronosUser',
      'serviceOrderEdit',
      'serviceOrderCustomDiscountEdit',
      'serviceOrderCustomerContractEdit',
      'serviceOrderFinancesProfitabilityRead',
      'serviceOrderVehicleClassEdit',
      'serviceOrderCustomSmallUnitMaterialEdit',
      'serviceOrderCancel',
    ],
    scopes: {
      serviceOrderEdit: {
        branchId: activeBranchId,
        serviceCaseType: serviceCaseTypeId,
        serviceOrderType: serviceOrderTypeId,
      },
      serviceOrderCustomDiscountEdit: {
        branchId: props.serviceCase?.branchId ?? '',
        serviceCaseType: serviceCaseTypeId,
        serviceOrderType: serviceOrderTypeId,
      },
      serviceOrderCustomerContractEdit: {
        branchId: props.serviceCase?.branchId ?? '',
        serviceCaseType: serviceCaseTypeId,
        serviceOrderType: serviceOrderTypeId,
      },
      serviceOrderFinancesProfitabilityRead: {
        branchId: props.serviceCase?.branchId ?? '',
        serviceCaseType: serviceCaseTypeId,
        serviceOrderType: serviceOrderTypeId,
      },
      serviceOrderVehicleClassEdit: {
        branchId: props.serviceCase?.branchId ?? '',
        serviceCaseType: serviceCaseTypeId,
        serviceOrderType: serviceOrderTypeId,
      },
      serviceOrderCustomSmallUnitMaterialEdit: {
        branchId: props.serviceCase?.branchId ?? '',
        serviceCaseType: serviceCaseTypeId,
        serviceOrderType: serviceOrderTypeId,
      },
      serviceOrderCancel: {
        branchId: props.serviceCase?.branchId ?? '',
        serviceCaseType: serviceCaseTypeId,
        serviceOrderType: serviceOrderTypeId,
      },
    },
  });

  // Cancelling an order is additionally blocked for users who cannot cancel the whole service case
  const canCancelOrder = props.canCancelServiceCase && canCancelServiceOrder;

  const handleSaveActionRequest = async () => await orderDetailRef.current?.submit();

  const refreshServiceOrderDocumentDataGrid = () =>
    refreshDmsDatagrid('document-context-list-service-case-order');

  useEffect(
    function leaveDisabledCheckoutTab() {
      if (
        isFalse(serviceOrder?.isCheckoutEnabled) &&
        orderTabId === ORDER_TABS.CHECKOUT &&
        serviceOrder?.id === orderId
      ) {
        setOrderTabId(ORDER_TABS.JOBS);
      }
    },
    [orderTabId, setOrderTabId, serviceOrder?.isCheckoutEnabled, orderId, serviceOrder?.id]
  );

  const {stateFlag, markersFlags, timeTrackingFlag, sparePartFlag, isOrderReadOnly} =
    useOrderCardFlags({
      serviceOrder: props.defaultServiceOrder,
      canReadChronos,
      canEditServiceOrder,
      activeBranchId,
      serviceCase: props.serviceCase,
    });

  const isOrderContentReadOnly =
    isOrderReadOnly || isLoading || isError || isNil(serviceOrder) || isNil(orderVariant);

  // A rejected poll keeps the last payload, so a transient error must not disable the action.
  const isReportButtonDisabled =
    isNil(integrations) ||
    integrations?.orderIntegration
      ?.find((integration) => integration?.integration?.code === 'CHRONOS')
      ?.action?.find((action) => action.key === 'chronosContractReport')?.style === 'disabled';

  const {badgeSettings: checklistBadge} = useChecklist({
    recordId:
      props.isExpanded && (isBootstrapSettled || orderTabId === ORDER_TABS.CHECKLISTS)
        ? props.serviceOrderId
        : '',
    resourceId: 'SERVICE_ORDER',
  });

  const {badgeSettings: auditsBadge} = useRecordAuditsBadge({
    recordId:
      props.isExpanded && (isBootstrapSettled || orderTabId === ORDER_TABS.AUDITS)
        ? props.serviceOrderId
        : '',
    resourceId: EntityResourceIds.serviceOrder,
  });

  const maximization = {
    enabled: true,
    title: `${i18n.t('entity.order.labels.serviceOrder')} ${serviceOrder?.number ?? props.defaultServiceOrder?.number ?? ''}`,
  };

  const tabs: TabProps[] = [
    {
      title: i18n.t('entity.orderRequest.labels.listTitle'),
      id: ORDER_TABS.JOBS,
      'data-testid': suffixTestId('requests', props),
      content: (
        <OrderJobs
          data-testid={suffixTestId('jobs', props)}
          orderId={props.serviceOrderId}
          orderVariantId={
            serviceOrder?.serviceOrderVariantId ??
            props.defaultServiceOrder?.serviceOrderVariant?.serviceOrderVariantId ??
            undefined
          }
          serviceCaseId={serviceCaseId}
          isReadOnly={isOrderContentReadOnly}
          datagridKey={`order-jobs-${props.dataGridKey}`}
          isReportButtonDisabled={isReportButtonDisabled}
        />
      ),
    },
    {
      title: i18n.t('entity.order.labels.detailTitle'),
      id: ORDER_TABS.DETAIL,
      'data-testid': suffixTestId('detail', props),
      content: (
        <OrderDetail
          ref={orderDetailRef}
          data-testid={suffixTestId('detail', props)}
          orderId={props.serviceOrderId}
          serviceCaseId={serviceCaseId}
          isReadOnly={isOrderContentReadOnly}
          serviceOrder={serviceOrder}
          orderVariants={variants}
          orderIntegrations={integrations ?? {orderIntegration: []}}
          orderType={type}
          isLoading={isLoading || isIntegrationsLoading}
          isError={isError || (isIntegrationsError && isNil(integrations))}
        />
      ),
    },
    {
      title: i18n.t('entity.order.labels.discounts'),
      id: ORDER_TABS.DISCOUNTS,
      'data-testid': suffixTestId('discounts', props),
      content: (
        <Discounts
          data-testid={suffixTestId('discounts', props)}
          serviceOrderId={props.serviceOrderId}
          serviceCaseId={serviceCaseId}
          isReadOnly={isOrderContentReadOnly}
          canEditDiscount={canEditSOCustomDiscount}
          canEditCustomerContract={canEditSOCustomerContract}
          canEditVehicleClass={canEditSOVehicleClass}
          canEditCustomSmallUnitMaterial={canEditSOCustomSmallUnitMaterial}
          orderType={type}
        />
      ),
    },
    {
      title: i18n.t('entity.orderRequest.labels.allItems'),
      id: ORDER_TABS.ALL_ITEMS,
      'data-testid': suffixTestId('items', props),
      content: (
        <OrderItemsDataGrid
          maximization={maximization}
          data-testid={suffixTestId('allJobs', props)}
          orderId={props.serviceOrderId}
          serviceCaseId={serviceCaseId}
          isReadOnly={isOrderContentReadOnly}
          key={`order-items-${props.dataGridKey}`}
        />
      ),
    },
    {
      title: i18n.t('general.labels.documents'),
      id: ORDER_TABS.DOCUMENTS,
      'data-testid': suffixTestId('documents', props),
      content: (
        <ServiceOrderDocuments
          maximization={maximization}
          refreshDataGrid={refreshServiceOrderDocumentDataGrid}
        />
      ),
    },
    {
      title: i18n.t('entity.order.labels.partsMovements'),
      id: ORDER_TABS.PARTS_MOVEMENTS,
      icon: getPartsMovementsTabIcon(serviceOrder?.partsMovements),
      'data-testid': suffixTestId('partsMovements', props),
      content: (
        <PartsMovements
          maximization={maximization}
          orderId={props.serviceOrderId}
          serviceCaseId={serviceCaseId}
        />
      ),
    },
    ...(isFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST)
      ? [
          {
            title: i18n.t('entity.checklist.labels.title'),
            id: ORDER_TABS.CHECKLISTS,
            badge: checklistBadge,
            content: (
              <Checklists
                recordId={props.serviceOrderId}
                resourceId="SERVICE_ORDER"
                contextTarget="service-case-order"
                contextId={props.serviceOrderId}
                isReadOnly={isOrderContentReadOnly}
              />
            ),
            'data-testid': suffixTestId('checklists', props),
          },
        ]
      : []),
    ...(isFeatureEnabled(featureFlags.CORE_CHECKLIST_AUDITS) &&
    isNotNil(props.serviceCase?.branchId)
      ? [
          {
            title: i18n.t('module.recordAudit.title'),
            id: ORDER_TABS.AUDITS,
            badge: auditsBadge,
            content: (
              <RecordAudits
                resourceId={EntityResourceIds.serviceOrder}
                recordId={props.serviceOrderId}
                // Audits belong to the service case's branch, not the user's active branch.
                branchId={props.serviceCase.branchId}
                // Deliberately NOT gated by isOrderContentReadOnly: audits are QC over finished work,
                // so they stay editable/creatable even on closed or locked orders. Editability
                // is governed by the audit's own state (fill manager allows edits only while
                // OPEN) and by backend authorization.
                // The tab already lives inside the order card — no nested card chrome.
                variant="flat"
                data-testid={suffixTestId('audits', props)}
              />
            ),
            'data-testid': suffixTestId('audits', props),
          },
        ]
      : []),
    {
      title: i18n.t('entity.order.labels.commentsAndNotes'),
      id: ORDER_TABS.COMMENTS_NOTES,
      'data-testid': suffixTestId('note', props),
      content: (
        <Stack direction={['column', 'column', 'column', 'row']} spacing={4} width="100%">
          <Box flex={1}>
            <CommentsWidget
              cardVariant="inlineWhite"
              resourceId={EntityResourceIds.serviceOrder}
              recordId={props.serviceOrderId}
              isEditDisabled={isOrderContentReadOnly}
              isAddDisabled={isOrderContentReadOnly}
              isDeleteDisabled={isOrderContentReadOnly}
              data-testid={suffixTestId('note', props)}
            />
          </Box>
          <Box flex={1}>
            <MetadaNoteWidget
              resourceId={EntityResourceIds.serviceOrder}
              contextId={props.serviceOrderId}
              context="COMMENTS_INTERNAL_NOTE"
              data-testid={suffixTestId('note', props)}
              isEditable={!isOrderContentReadOnly}
            />
          </Box>
        </Stack>
      ),
    },
    {
      title: i18n.t('entity.order.labels.profitability'),
      id: ORDER_TABS.PROFITABILITY,
      'data-testid': suffixTestId('profitability', props),
      content: (
        <VStack spacing={4}>
          <ProfitabilitySummary
            isReportButtonDisabled={isReportButtonDisabled}
            serviceCaseId={serviceCaseId}
            canReadServiceOrderFinancesProfitability={canReadServiceOrderFinancesProfitability}
            data-testid={suffixTestId('profitability', props)}
          />
          <Show when={canReadServiceOrderFinancesProfitability}>
            <Heading size={4}>{i18n.t('entity.order.labels.orderProfitability')}</Heading>
            <ProfitabilityDataGrid
              maximization={maximization}
              orderId={props.serviceOrderId}
              serviceCaseId={serviceCaseId}
            />
          </Show>
        </VStack>
      ),
    },
    {
      title: i18n.t('general.labels.activityLog'),
      id: ORDER_TABS.ACTIVITY_LOG,
      'data-testid': suffixTestId('activityLog', props),
      content: (
        <ActivityLogDataGrid
          maximization={maximization}
          orderId={props.serviceOrderId}
          data-testid={suffixTestId('activityLog', props)}
        />
      ),
    },
    {
      title: i18n.t('entity.order.labels.checkout'),
      id: ORDER_TABS.CHECKOUT,
      isDisabled: isFalsy(serviceOrder?.isCheckoutEnabled),
      'data-testid': suffixTestId('checkout', props),
      content: <Checkout data-testid={suffixTestId('checkout', props)} />,
    },
  ];

  const priceDifference = getPriceDifference(
    props.defaultServiceOrder?.totalSalesPrice?.withVat?.amount,
    props.defaultServiceOrder?.totalPriceEstimated?.amount
  );
  const isOverpriced = isNotNil(priceDifference) && priceDifference > OVERPRICED_THRESHOLD;

  const estimatedPriceAmount = props.defaultServiceOrder?.totalPriceEstimated?.amount;
  const formattedEstimatedPrice = isNil(estimatedPriceAmount)
    ? EMPTY_PLACEHOLDER
    : formatCurrency(
        estimatedPriceAmount,
        props.defaultServiceOrder?.totalPriceEstimated?.currency ?? tenant.tenantCurrency,
        2
      );
  const priceWithoutVat = props.defaultServiceOrder?.totalSalesPrice?.withoutVat;
  const formattedPriceWithoutVat = isNil(priceWithoutVat?.amount)
    ? EMPTY_PLACEHOLDER
    : formatCurrency(priceWithoutVat.amount, priceWithoutVat.currency ?? tenant.tenantCurrency, 2);
  const priceWithVat = props.defaultServiceOrder?.totalSalesPrice?.withVat;
  const formattedPriceWithVat = isNil(priceWithVat?.amount)
    ? EMPTY_PLACEHOLDER
    : formatCurrency(priceWithVat.amount, priceWithVat.currency ?? tenant.tenantCurrency, 2);

  return (
    <Card colorStripe={getColorStripe(type)}>
      <Clickable onClick={props.onExpand} isTextSelectable>
        <HStack spacing={3} align="center">
          <Avatar size="default" variant="square" icon="sidebar/workshop_service_orders" />
          <Box flex={1}>
            <HStack align="flex-start" justify="space-between">
              <HStack spacing={3} align="center">
                <Heading size={3} data-testid={suffixTestId('name', props)}>
                  {defaultTo('-', props.defaultServiceOrder?.number)}
                </Heading>
                <Flags
                  size="small"
                  spacing={1}
                  flags={concatAll<FlagProps[]>([
                    buildArray<FlagProps>()
                      .when(isNotNil(stateFlag), stateFlag as FlagProps)
                      .when(isNotNil(timeTrackingFlag), timeTrackingFlag!)
                      .when(props.defaultServiceOrder?.isCustomerSyncEnabled, {
                        label: i18n.t('entity.order.labels.differentCustomer'),
                        colorScheme: 'red',
                      })
                      .when(isNotNil(sparePartFlag), sparePartFlag!),
                    markersFlags,
                  ])}
                  data-testid={suffixTestId('state', props)}
                />
              </HStack>
              <OrderActions
                orderId={props.serviceOrderId}
                serviceOrder={props.defaultServiceOrder}
                isExpanded={props.isExpanded}
                onExpand={props.onExpand}
                onDataChange={props.onDataChange}
                onSaveRequest={handleSaveActionRequest}
                isSaveRequestPending={isSaveRequestPending}
                onSaveRequestPendingChange={setIsSaveRequestPending}
                refreshServiceOrderDocumentDataGrid={refreshServiceOrderDocumentDataGrid}
                isOrderReadOnly={isOrderReadOnly}
                canCancelOrder={canCancelOrder}
                data-testid={props['data-testid']}
              />
            </HStack>
            <Space vertical={1} />
            <HStack align="flex-start" justify="space-between">
              <Tooltip label={i18n.t('entity.order.labels.serviceOrderVariant')}>
                <Text size="small" color="secondary">
                  {props.defaultServiceOrder?.serviceOrderVariant?.name || EMPTY_PLACEHOLDER}
                </Text>
              </Tooltip>
              <HStack spacing={1}>
                <Tooltip label={i18n.t('general.labels.estimated')}>
                  <Text
                    size="small"
                    color="secondary"
                    data-testid={suffixTestId('totalPriceEstimated', props)}
                  >
                    {formattedEstimatedPrice}
                  </Text>
                </Tooltip>
                <Text size="small" color="secondary">
                  {DOT_CHARACTER}
                </Text>
                <PriceDetailTooltip
                  priceType="withoutVat"
                  materialPrice={props.defaultServiceOrder?.materialSalesPrice}
                  labourPrice={props.defaultServiceOrder?.labourSalesPrice}
                  smallUnitMaterialPrice={props.defaultServiceOrder?.smallUnitMaterialSalesPrice}
                  data-testid={suffixTestId('priceWithoutVatTooltip', props)}
                >
                  <Text
                    size="small"
                    color="secondary"
                    data-testid={suffixTestId('totalPriceWithoutVat', props)}
                  >
                    {formattedPriceWithoutVat} {i18n.t('general.labels.withoutVat')}
                  </Text>
                </PriceDetailTooltip>
                <Text size="small" color="secondary">
                  {DOT_CHARACTER}
                </Text>
                <PriceDetailTooltip
                  materialPrice={props.defaultServiceOrder?.materialSalesPrice}
                  labourPrice={props.defaultServiceOrder?.labourSalesPrice}
                  smallUnitMaterialPrice={props.defaultServiceOrder?.smallUnitMaterialSalesPrice}
                  data-testid={suffixTestId('priceTooltip', props)}
                >
                  <Text
                    size="small"
                    alternative
                    color={isOverpriced ? 'danger' : undefined}
                    data-testid={suffixTestId('totalPriceWithVat', props)}
                  >
                    {formattedPriceWithVat} {i18n.t('general.labels.withVat')}
                    {isOverpriced && ` (${formatNumber(priceDifference, 1)}%)`}
                  </Text>
                </PriceDetailTooltip>
              </HStack>
            </HStack>
          </Box>
        </HStack>
        <Separator />
        <HStack spacing={4} align="center">
          <ReadyToInvoiceSwitch
            serviceCaseId={serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            serviceOrder={props.defaultServiceOrder}
            vehicleId={props.serviceCase?.vehicleId ?? ''}
            isSaveRequestPending={isSaveRequestPending}
            isOrderDataRefetching={props.isOrderDataRefetching}
            refreshServiceOrderDocumentDataGrid={refreshServiceOrderDocumentDataGrid}
            data-testid={props['data-testid']}
          />
          <Box flex={1}>
            <OrderInformation
              serviceOrder={props.defaultServiceOrder}
              customerContractName={props.defaultServiceOrder?.customerContract?.name}
              isReadOnly={isOrderReadOnly}
              data-testid={suffixTestId('contactInfo', props)}
            />
          </Box>
        </HStack>
      </Clickable>
      <Show when={props.isExpanded}>
        <Separator />
        <OrderCardTabs
          onChange={setOrderTabId}
          activeTabId={orderTabId}
          tabs={tabs}
          isLoading={isLoading}
          isError={isError}
          data-testid={suffixTestId('tabs', props)}
        />
      </Show>
    </Card>
  );
}

const getPriceDifference = (realPrice: number | Nullish, estimatedPrice: number | Nullish) =>
  isPositive(realPrice) && isPositive(estimatedPrice)
    ? (realPrice / estimatedPrice - DIFFERENCE_OFFSET) * PERCENTAGE_MULTIPLIER
    : null;

const getColorStripe = (type: string | undefined | null) =>
  match(type)
    .with('COM', () => 'blue' as const)
    .with('INS', () => 'magenta' as const)
    .with('WAR', () => 'orange' as const)
    .with('CLA', () => 'red' as const)
    .with('CON', () => 'green' as const)
    .with('INT', () => 'teal' as const)
    .otherwise(() => undefined);
